import { useState } from "react";
import { Newspaper } from "lucide-react";
import { ArticleCard } from "@/components/public/ArticleCard";
import { useI18n } from "@/lib/i18n";
import { useArticles } from "@/hooks/useArticles";
import { adaptArticles } from "@/lib/adapters";
import { useSeo } from "@/hooks/useSeo";

function RecapHebdomadaire() {
  const { t, locale } = useI18n();

  useSeo({
    title: `${t("recap.heading")} — Ramba News`,
    description: t("recap.subtitle"),
    path: "/recap-hebdomadaire",
  });

  // Calculé une seule fois (lazy initializer) : recalculer `new Date()` à
  // chaque rendu changerait la clé de cache de useArticles à chaque fois
  // (timestamp différent à la milliseconde près), déclenchant une requête —
  // donc un re-rendu — donc une nouvelle date — en boucle infinie.
  const [publieDepuis] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString();
  });

  const { data, isLoading } = useArticles(0, 100, "PUBLIE", { publieDepuis });

  const rawArticles = Array.isArray(data) ? data : data?.content ?? [];
  const articles = adaptArticles(rawArticles);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 md:py-14">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-verified">
        {locale === "fr" ? "Récap" : "Recap"}
      </div>
      <h1 className="mt-2 font-serif text-3xl md:text-5xl font-semibold tracking-tight">
        {t("recap.heading")}
      </h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">{t("recap.subtitle")}</p>

      {isLoading ? (
        <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-3 animate-pulse">
              <div className="aspect-[16/9] rounded-md bg-muted" />
              <div className="h-4 rounded bg-muted" />
              <div className="h-4 w-3/4 rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="mt-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
          <Newspaper className="h-8 w-8 opacity-25" />
          <p className="text-sm">{t("recap.empty")}</p>
        </div>
      ) : (
        <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {articles.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </div>
      )}
    </div>
  );
}

export default RecapHebdomadaire;
